import React, { useEffect, useState } from 'react';
import {
  Typography,
  Breadcrumb,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  DatePicker,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const ClientPlans = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [form] = Form.useForm();

  const API_BASE_URL = '/api/billing';

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Client ID', dataIndex: 'client_id', key: 'client_id', width: 100 },
    { title: 'Stripe Customer', dataIndex: 'stripe_customer_id', key: 'stripe_customer_id' },
    { title: 'Plan', dataIndex: 'plan', key: 'plan' },
    { title: 'Status', dataIndex: 'subscription_status', key: 'subscription_status', render: (s) => (
        <Tag color={s === 'active' ? 'green' : s === 'trialing' ? 'blue' : s === 'past_due' ? 'orange' : s === 'canceled' ? 'red' : 'default'}>{s || '—'}</Tag>
      )
    },
    { title: 'Next Billing', dataIndex: 'next_billing_date', key: 'next_billing_date', width: 160 },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this billing record?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(record.id)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const fetchBilling = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset: (page - 1) * pageSize });
      const { data: res } = await axios.get(`${API_BASE_URL}?${params.toString()}`);
      const items = (res.items || []).filter(r => r.client_id != null);
      setData(items);
      setPagination({ current: page, pageSize, total: items.length });
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBilling(1, pagination.pageSize); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = (pag) => {
    fetchBilling(pag.current, pag.pageSize);
  };

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      client_id: record.client_id,
      stripe_customer_id: record.stripe_customer_id,
      subscription_status: record.subscription_status,
      plan: record.plan,
      next_billing_date: record.next_billing_date ? dayjs(record.next_billing_date) : null,
    });
    setOpen(true);
  };

  const authHeader = () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/${id}`, { headers: authHeader() });
      message.success('Billing record deleted');
      fetchBilling(pagination.current, pagination.pageSize);
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        client_id: values.client_id ?? null,
        stripe_customer_id: values.stripe_customer_id ?? null,
        subscription_status: values.subscription_status ?? null,
        plan: values.plan ?? null,
        next_billing_date: values.next_billing_date?.format ? values.next_billing_date.format('YYYY-MM-DD') : values.next_billing_date || null,
      };

      if (editing?.id) {
        await axios.put(`${API_BASE_URL}/${editing.id}`, payload, { headers: authHeader() });
        message.success('Billing updated');
      } else {
        await axios.post(`${API_BASE_URL}`, payload, { headers: authHeader() });
        message.success('Billing created');
      }
      setOpen(false);
      setEditing(null);
      fetchBilling(pagination.current, pagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Billing' },
        { title: 'Client Plans' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Client Subscription Plans</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>New Billing</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage billing records for individual clients only (excluding agency-level records).
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={onTableChange}
        />
      </Card>

      <Modal
        open={open}
        title={editing ? 'Edit Billing' : 'Create Billing'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Client ID" name="client_id">
            <InputNumber style={{ width: '100%' }} placeholder="Client ID (optional)" />
          </Form.Item>
          <Form.Item label="Agency ID" name="agency_id">
            <InputNumber style={{ width: '100%' }} placeholder="Agency ID (optional)" />
          </Form.Item>
          <Form.Item label="Stripe Customer ID" name="stripe_customer_id">
            <Input placeholder="e.g., cus_..." />
          </Form.Item>
          <Form.Item label="Subscription Status" name="subscription_status">
            <Select allowClear placeholder="Select status (optional)">
              <Option value="trialing">Trialing</Option>
              <Option value="active">Active</Option>
              <Option value="past_due">Past Due</Option>
              <Option value="canceled">Canceled</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Plan" name="plan">
            <Input placeholder="e.g., starter, pro, enterprise" />
          </Form.Item>
          <Form.Item label="Next Billing Date" name="next_billing_date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientPlans;
